/**
 * ImportBatchActions — the done-screen actions for one import run.
 *
 * Every import run stamps one batch id on the items it creates
 * (items.import_batch_id, see src/lib/importBatch.ts). This component gives
 * the done screen its two follow-ups:
 *   - "View these N items" → the items list filtered to that batch
 *     (`/prioritization/items?importBatch=<id>`)
 *   - "Undo this import"   → one reason prompt, then every item of the batch is
 *     retired through the governed retire_new_item path (never a hard delete),
 *     reporting "Retired N of M — K could not be retired (already in use)".
 * It also reports rows skipped as already imported.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { RetireItemsDialog } from '@/components/reversals/RetireItemsDialog';
import { useItemsStore, type RetireItemFailure } from '@/stores/prioritization/itemsStore';
import { describeSkippedAsImported, importBatchListHref } from '@/lib/importBatch';

export interface ImportBatchActionsProps {
  batchId: string;
  /** Items this run created. */
  itemIds: string[];
  /** Rows skipped because an earlier, un-retired import already created them. */
  skipped?: number;
  /** Called before navigating away (e.g. close the modal). */
  onNavigate?: () => void;
  /**
   * Runs after the reason is given, before items are retired — dissolve the
   * relationships / collection this import created so they don't block it.
   */
  beforeUndo?: () => Promise<void>;
  /** Called with the undo outcome (all, some or none retired). */
  onUndone?: (outcome: { retired: string[]; failed: RetireItemFailure[] }) => void;
}

export function ImportBatchActions({
  batchId,
  itemIds,
  skipped = 0,
  onNavigate,
  beforeUndo,
  onUndone,
}: ImportBatchActionsProps) {
  const navigate = useNavigate();
  const allItems = useItemsStore((s) => s.items);
  const [undoOpen, setUndoOpen] = useState(false);
  const [remaining, setRemaining] = useState<string[]>(itemIds);
  const [undone, setUndone] = useState(false);

  const targets = useMemo(() => {
    const byId = new Map(allItems.map((i) => [i.id, i.title]));
    return remaining.map((id) => ({ id, title: byId.get(id) }));
  }, [allItems, remaining]);

  const n = itemIds.length;
  const skippedNote = describeSkippedAsImported(skipped);

  return (
    <div className="flex flex-col items-center gap-2">
      {skippedNote && <p className="text-sm text-[var(--text-ter)]">{skippedNote}.</p>}
      {undone ? (
        <p className="text-sm italic text-[var(--text-ter)]">
          This import was undone. Its items left every list; their records and history stay.
        </p>
      ) : n > 0 ? (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              onNavigate?.();
              navigate(importBatchListHref(batchId));
            }}
          >
            <List className="h-4 w-4" />
            View these {n} item{n === 1 ? '' : 's'}
          </Button>
          {remaining.length > 0 && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setUndoOpen(true)}>
              <RotateCcw className="h-4 w-4" />
              Undo this import
            </Button>
          )}
        </div>
      ) : null}

      <RetireItemsDialog
        open={undoOpen}
        onClose={() => setUndoOpen(false)}
        items={targets}
        title="Undo this import"
        confirmLabel={`Retire ${targets.length} item${targets.length === 1 ? '' : 's'}`}
        explain={
          <>
            The {targets.length} item{targets.length === 1 ? '' : 's'} this import created will leave every
            list. Their records and history stay. Items something already uses (a pool, session, plan or
            decision) can&apos;t be retired — you can archive those instead.
          </>
        }
        beforeRetire={beforeUndo}
        onFinished={(outcome) => {
          const left = outcome.failed.map((f) => f.id);
          setRemaining(left);
          if (left.length === 0) setUndone(true);
          onUndone?.(outcome);
        }}
      />
    </div>
  );
}
